/**
 * Small presentational primitives shared by every view — the repeating shapes of
 * the approved mock-up: uppercase eyebrows, section headings, stat blocks, pills,
 * breadcrumbs, CTA buttons and the transient toast.
 */
import { append, el, type Child } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import type { AppRouteKey, RouteParams } from "../core/routes.js";

/** The mock-up's small uppercase olive label above a heading. */
export function eyebrow(label: string): HTMLElement {
	return el("p", { class: "mb-eyebrow" }, label);
}

export interface SectionHeadProps {
	eyebrow?: string;
	title: string;
	lead?: string;
	/** Optional trailing link, rendered right-aligned on wide screens. */
	link?: { label: string; key: AppRouteKey; params?: RouteParams };
	/** Heading level; sections use h2, page heroes use h1. */
	level?: "h1" | "h2";
	className?: string;
}

export function sectionHead(ctx: AppContext, props: SectionHeadProps): HTMLElement {
	const text = el("div", { class: "mb-sectionhead__text" });
	if (props.eyebrow) text.appendChild(eyebrow(props.eyebrow));
	text.appendChild(el(props.level ?? "h2", { class: "mb-sectionhead__title" }, props.title));
	if (props.lead) text.appendChild(el("p", { class: "mb-sectionhead__lead" }, props.lead));

	const head = el(
		"div",
		{ class: `mb-sectionhead${props.className ? ` ${props.className}` : ""}` },
		text,
	);
	if (props.link) {
		head.appendChild(
			el(
				"a",
				{
					class: "mb-inlinelink",
					href: ctx.href(props.link.key, props.link.params),
					"data-routekey": props.link.key,
				},
				props.link.label,
			),
		);
	}
	return head;
}

/** A figure + caption pair, as used for the mock-up's numeric proof points. */
export function statBlock(
	value: string,
	label: string,
	variant: "bar" | "plain" = "bar",
): HTMLElement {
	return el(
		"div",
		{ class: `mb-stat mb-stat--${variant}` },
		el("span", { class: "mb-stat__value" }, value),
		el("span", { class: "mb-stat__label" }, label),
	);
}

/** A rounded chip. `tone` picks the accent (lime) or neutral treatment. */
export function pill(label: string, tone: "accent" | "neutral" = "neutral"): HTMLElement {
	return el("span", { class: `mb-pill mb-pill--${tone}` }, label);
}

export interface CrumbSpec {
	label: string;
	key?: AppRouteKey;
	params?: RouteParams;
}

/** Breadcrumb trail. The last entry renders as plain text (current page). */
export function breadcrumbs(ctx: AppContext, crumbs: CrumbSpec[]): HTMLElement {
	const list = el("ol", { class: "mb-crumbs__list" });
	crumbs.forEach((crumb, i) => {
		const isLast = i === crumbs.length - 1;
		const item = el("li", { class: "mb-crumbs__item" });
		if (crumb.key && !isLast) {
			item.appendChild(
				el("a", { href: ctx.href(crumb.key, crumb.params), class: "mb-crumbs__link" }, crumb.label),
			);
		} else {
			item.appendChild(
				el("span", { class: "mb-crumbs__current", "aria-current": isLast && "page" }, crumb.label),
			);
		}
		list.appendChild(item);
	});
	return el("nav", { class: "mb-crumbs", "aria-label": ctx.copy().a11y.breadcrumb }, list);
}

export type CtaVariant = "accent" | "outline" | "solid" | "quiet";

/** A link styled as a call-to-action button. */
export function ctaLink(
	ctx: AppContext,
	label: string,
	key: AppRouteKey,
	variant: CtaVariant = "accent",
	params?: RouteParams,
): HTMLElement {
	return el(
		"a",
		{
			class: `mb-cta mb-cta--${variant}`,
			href: ctx.href(key, params),
			"data-routekey": key,
		},
		label,
	);
}

/** A button styled as a call-to-action (for actions that are not navigation). */
export function ctaButton(
	label: string,
	onClick: () => void,
	variant: CtaVariant = "accent",
	extra?: Record<string, string>,
): HTMLElement {
	return el(
		"button",
		{ class: `mb-cta mb-cta--${variant}`, type: "button", onClick, ...extra },
		label,
	);
}

/** Section wrapper carrying the mock-up's alternating paper/white surfaces. */
export function section(
	tone: "paper" | "white" | "glow",
	children: Child[],
	attrs?: Record<string, string>,
): HTMLElement {
	const inner = el("div", { class: "mb-section__inner" });
	append(inner, children);
	return el("section", { class: `mb-section mb-section--${tone}`, ...attrs }, inner);
}

/** The lime "✦" bullet the mock-up uses in scope/benefit lists. */
export function markerList(items: string[]): HTMLElement {
	const list = el("ul", { class: "mb-marklist" });
	for (const item of items) {
		list.appendChild(
			el(
				"li",
				{ class: "mb-marklist__item" },
				el("span", { class: "mb-marklist__marker", "aria-hidden": "true" }, "✦"),
				el("span", null, item),
			),
		);
	}
	return list;
}

/** Chips for material/product groups. */
export function chipRow(items: string[]): HTMLElement {
	const row = el("ul", { class: "mb-chiprow" });
	for (const item of items) {
		row.appendChild(el("li", { class: "mb-chiprow__item" }, item));
	}
	return row;
}

let toastHost: HTMLElement | null = null;

/**
 * Show a transient status message. Uses role="status" so assistive technology
 * announces it without stealing focus.
 */
export function toast(message: string): void {
	if (typeof document === "undefined") return;
	if (!toastHost || !toastHost.isConnected) {
		toastHost = el("div", { class: "mb-toasts", role: "status", "aria-live": "polite" });
		document.body.appendChild(toastHost);
	}
	const item = el("div", { class: "mb-toast" }, message);
	toastHost.appendChild(item);
	window.setTimeout(() => {
		item.classList.add("is-leaving");
		window.setTimeout(() => item.remove(), 300);
	}, 4000);
}

/** A loading placeholder used while a view awaits the gateway. */
export function skeleton(lines = 3): HTMLElement {
	const wrap = el("div", { class: "mb-skeleton", "aria-hidden": "true" });
	for (let i = 0; i < lines; i += 1) wrap.appendChild(el("span", { class: "mb-skeleton__line" }));
	return wrap;
}
